import React, { useState, useRef, useEffect } from "react";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import { EmtIcon } from "./EmtIcon";
import Autocomplete, {
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { EmtTypography } from "./EmtTypography";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { MenuItemBox, StyledMenuItem } from "./EmtSelect";
import { EmtLoader } from "./EmtLoader";
import { EmtPaper } from "./EmtPaper";
import CircularProgress from "@mui/material/CircularProgress";

export type EmtAutocompleteOption = {
  id: string;
  label: string;
  media?: React.ReactNode;
  caption?: string;
};

export type EmtAutocompleteProps = Omit<
  AutocompleteProps<EmtAutocompleteOption, boolean, boolean, boolean>,
  "renderInput"
> & {
  helperText?: string;
  /**
   * @default false
   */
  error?: boolean;
  label: string;
  /**
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Type of the icon
   * @default dropdown
   */
  iconType?: "dropdown" | "location" | "search";

  iconLeftClassName?: string;
  /**
   * Event trigger when icon is clicked
   * @default undefined
   */
  onIconClick?: () => void;
  /**
   * Define if loader is active or not
   * @default false
   */
  loader?: boolean;
  nonce?: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

export function EmtAutocomplete(props: EmtAutocompleteProps) {
  const {
    helperText,
    error = false,
    label,
    fullWidth = false,
    onClick,
    onClose,
    onIconClick,
    disableClearable,
    iconType = "dropdown",
    iconLeftClassName,
    loader = false,
    open,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open === false) inputRef.current?.blur();
  }, [open]);

  let iconClassName: string;
  let activeIconClassName: string;
  switch (iconType) {
    case "search":
      iconClassName = "emt-search-1";
      activeIconClassName = "emt-search-1";
      break;
    case "location":
      iconClassName = "emt-location-1";
      activeIconClassName = "emt-location-1";
      break;
    case "dropdown":
    default:
      iconClassName = "emt-down-1";
      activeIconClassName = "emt-up-1";
      break;
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsOpen(false);
        inputRef.current?.blur();
      }}
    >
      <FormControl
        error={error}
        fullWidth={fullWidth}
        onClick={(e) => {
          setIsOpen(true);
          onClick ? onClick(e) : null;
        }}
      >
        {/* @ts-ignore */}
        <Autocomplete
          {...rest}
          blurOnSelect
          forcePopupIcon={false}
          onChange={(e, value, reason, details) => {
            rest.onChange?.(
              e as any,
              value as any,
              reason as any,
              details as any
            );
            // ferme clavier
            inputRef.current?.blur();
            // ferme l'état interne (utile pour l’icône)
            setIsOpen(false);
          }}
          onClose={(e) => {
            setIsOpen(false);
            inputRef.current?.blur();
            onClose ? onClose(e, "escape") : null;
          }}
          getOptionLabel={(option: any) =>
            typeof option === "string" ? option : option?.label
          }
          isOptionEqualToValue={(option: any, value: any) => {
            return option.label === value?.label;
          }}
          renderInput={(
            params: Omit<AutocompleteRenderInputParams, "inputProps"> & {
              inputProps: any;
            }
          ) => {
            return (
              <Box sx={{ position: "relative", width: "100%" }}>
                {/* Left icon */}
                {iconLeftClassName && (
                  <EmtIcon
                    className={iconLeftClassName}
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 26,
                      height: 26,
                      borderRadius: "12px",
                      backgroundColor: "#FBF1FE",
                      color: "#AE59C6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  />
                )}
                <TextField
                  {...params}
                  fullWidth={fullWidth}
                  label={label}
                  variant="filled"
                  sx={{
                    ...(iconLeftClassName && {
                      "& .MuiInputBase-root": {
                        paddingLeft: "44px",
                      },
                      "& .MuiInputLabel-root": {
                        paddingLeft: "50px",
                      },
                    }),
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        {loader ? (
                          <CircularProgress
                            size={16}
                            className={`emt-icons ${
                              !disableClearable &&
                              params.inputProps.value !== ""
                                ? "force-right"
                                : ""
                            }`}
                          />
                        ) : (
                          <EmtIcon
                            className={`${
                              isOpen ? activeIconClassName : iconClassName
                            } ${
                              !disableClearable &&
                              params.inputProps.value !== ""
                                ? "force-right"
                                : ""
                            }`}
                            onClick={(e: any) => {
                              e?.preventDefault?.();
                              e?.stopPropagation?.();
                              onIconClick?.();
                            }}
                            sx={{ cursor: onIconClick ? "pointer" : "default" }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            );
          }}
          renderOption={(props, option: any, { selected }) => (
            <StyledMenuItem {...props}>
              <MenuItemBox
                className={selected ? "active" : ""}
                sx={{
                  justifyContent: option.media
                    ? "space-arround"
                    : "space-between",
                }}
              >
                {option.media && (
                  <Box sx={{ display: "flex" }}>
                    <>{option.media}</>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: "0",
                  }}
                >
                  <EmtTypography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      flex: "1",
                    }}
                  >
                    {option.label}
                  </EmtTypography>
                  {option.caption && (
                    <EmtTypography variant="caption">
                      {option.caption}
                    </EmtTypography>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {selected && <EmtIcon className="emt-tick" />}
                </Box>
              </MenuItemBox>
            </StyledMenuItem>
          )}
          PaperComponent={(paperProps) =>
            loader ? (
              <EmtPaper sx={{ height: "60px" }}>
                <EmtLoader
                  size={22}
                  sx={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    top: "17px",
                  }}
                />
              </EmtPaper>
            ) : (
              <EmtPaper {...paperProps} />
            )
          }
        />
        <FormHelperText
          color={props.color}
          style={{
            fontFamily: "Nunito Sans",
          }}
        >
          {helperText}
        </FormHelperText>
      </FormControl>
    </ClickAwayListener>
  );
}
